import { useState, useEffect } from "react";
import { useParams } from "react-router-dom";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";
import { VoiceButton } from "@/components/VoiceButton";
import { ShieldAlert, Camera, Trash2, Plus, ArrowRight, ArrowLeft, CheckCircle2, Loader2 } from "lucide-react";

export default function PortalComplaint() {
  const { code } = useParams();
  const [complaint, setComplaint] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [step, setStep] = useState(0);
  const [extraNote, setExtraNote] = useState("");
  const [title, setTitle] = useState("");
  const [submitting, setSubmitting] = useState(false);

  const transcribe = async (blob) => {
    const fd = new FormData(); fd.append("file", blob, "audio.webm");
    const r = await api.post(`/public/klacht/${code}/transcribe`, fd);
    return r.data.text;
  };

  const load = () => {
    api.get(`/public/klacht/${code}`).then((r) => { setComplaint(r.data); setExtraNote(r.data.extra_note || ""); setTitle(r.data.title === "Klachtmelding door debiteur" ? "" : (r.data.title || "")); })
      .catch(() => setError("Deze link is ongeldig of verlopen.")).finally(() => setLoading(false));
  };
  useEffect(() => { load(); }, [code]); // eslint-disable-line

  const uploadPhoto = async (file, desc) => {
    const fd = new FormData(); fd.append("file", file); fd.append("description", desc || "");
    const r = await api.post(`/public/klacht/${code}/photos`, fd);
    setComplaint((c) => ({ ...c, photos: [...(c.photos || []), r.data] }));
    toast.success("Foto toegevoegd");
  };
  const delPhoto = async (pid) => {
    await api.delete(`/public/klacht/${code}/photos/${pid}`);
    setComplaint((c) => ({ ...c, photos: c.photos.filter((p) => p.id !== pid) }));
  };
  const submit = async () => {
    setSubmitting(true);
    try {
      await api.patch(`/public/klacht/${code}`, { extra_note: extraNote, title });
      await api.post(`/public/klacht/${code}/submit`);
      setStep(2);
    } catch (e) { toast.error(e?.response?.data?.detail || "Versturen mislukt"); }
    finally { setSubmitting(false); }
  };

  if (loading) return <div className="min-h-screen flex items-center justify-center text-slate-400"><Loader2 className="h-6 w-6 animate-spin" /></div>;
  if (error) return <div className="min-h-screen flex items-center justify-center p-6"><div className="text-center text-slate-600" data-testid="portal-complaint-error"><ShieldAlert className="h-10 w-10 mx-auto text-rose-500 mb-2" />{error}</div></div>;

  const submitted = complaint.submitted_at || step === 2;

  return (
    <div className="min-h-screen bg-[#F7F5EF]">
      <div className="bg-[#0F1E3D] text-white p-5 border-b-2 border-[#C9A227]">
        <div className="max-w-lg mx-auto flex items-center gap-3">
          <ShieldAlert className="h-6 w-6 text-[#C9A227]" />
          <div>
            <div className="font-heading text-lg font-semibold">Klachtmelding</div>
            <div className="text-slate-300 text-sm">{complaint.title}</div>
          </div>
        </div>
      </div>

      <div className="max-w-lg mx-auto p-5 space-y-4" data-testid="portal-complaint-page">
        {submitted ? (
          <div className="text-center py-10 space-y-3" data-testid="portal-complaint-done">
            <CheckCircle2 className="h-14 w-14 text-[#C9A227] mx-auto" />
            <h2 className="font-heading text-xl font-semibold text-slate-800">Bedankt!</h2>
            <p className="text-slate-500">Uw melding met {complaint.photos?.length || 0} foto('s) is ontvangen. De beheerder neemt dit in behandeling.</p>
          </div>
        ) : (
          <>
            {complaint.description && <div className="bg-white border rounded-xl p-4 text-sm text-slate-600">{complaint.description}</div>}

            {step === 0 && (
              <div className="space-y-4">
                <div className="bg-white border rounded-xl p-3 space-y-1.5">
                  <label className="block text-sm font-medium text-slate-700">Onderwerp van uw klacht</label>
                  <Input value={title} onChange={(e) => setTitle(e.target.value)} placeholder="bijv. Lekkage badkamer" data-testid="portal-title-input" />
                </div>
                <p className="text-sm text-slate-600">Maak foto's van de gebreken en geef bij elke foto een korte uitleg. U kunt de uitleg typen of inspreken. Voeg zoveel foto's toe als nodig.</p>
                <PortalPhotoAdder onUpload={uploadPhoto} transcribe={transcribe} />
                {complaint.photos?.length > 0 && (
                  <div className="space-y-2" data-testid="portal-photo-list">
                    {complaint.photos.map((p) => (
                      <div key={p.id} className="flex gap-3 bg-white border rounded-xl p-2" data-testid={`portal-photo-${p.id}`}>
                        <img src={p.url} alt="" className="h-16 w-16 object-cover rounded-lg" />
                        <div className="flex-1 text-sm text-slate-600">{p.description || <span className="text-slate-400">Geen uitleg</span>}</div>
                        <button className="text-red-500" onClick={() => delPhoto(p.id)} data-testid={`portal-photo-del-${p.id}`}><Trash2 className="h-4 w-4" /></button>
                      </div>
                    ))}
                  </div>
                )}
                <Button className="w-full bg-rose-600 hover:bg-rose-700" disabled={!(complaint.photos?.length)} onClick={() => setStep(1)} data-testid="portal-next">Volgende <ArrowRight className="h-4 w-4 ml-2" /></Button>
              </div>
            )}

            {step === 1 && (
              <div className="space-y-4">
                <label className="block font-medium text-slate-700">Wil je nog iets toevoegen?</label>
                <div className="flex items-start gap-2">
                  <Textarea rows={5} value={extraNote} onChange={(e) => setExtraNote(e.target.value)} placeholder="Aanvullende opmerkingen (optioneel)…" data-testid="portal-extra-input" />
                  <VoiceButton transcribe={transcribe} onText={(t) => setExtraNote((d) => (d ? d + " " : "") + t)} testId="portal-extra-voice" label="" />
                </div>
                <div className="flex gap-2">
                  <Button variant="outline" className="flex-1" onClick={() => setStep(0)}><ArrowLeft className="h-4 w-4 mr-2" /> Terug</Button>
                  <Button className="flex-1 bg-[#0F1E3D] hover:bg-[#1B2D52] text-white" onClick={submit} disabled={submitting} data-testid="portal-submit">{submitting ? "Versturen…" : "Versturen"}</Button>
                </div>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
}

function PortalPhotoAdder({ onUpload, transcribe }) {
  const [file, setFile] = useState(null);
  const [preview, setPreview] = useState("");
  const [desc, setDesc] = useState("");
  const [busy, setBusy] = useState(false);
  const pick = (e) => { const f = e.target.files?.[0]; if (f) { setFile(f); setPreview(URL.createObjectURL(f)); } };
  const submit = async () => {
    if (!file) return;
    setBusy(true);
    try { await onUpload(file, desc); setFile(null); setPreview(""); setDesc(""); }
    catch { toast.error("Uploaden mislukt"); }
    finally { setBusy(false); }
  };
  return (
    <div className="bg-white border rounded-xl p-3 space-y-2" data-testid="portal-photo-adder">
      {preview ? <img src={preview} alt="" className="h-48 w-full object-cover rounded-lg" /> : (
        <label className="flex flex-col items-center justify-center h-36 border-2 border-dashed rounded-lg cursor-pointer text-slate-400 active:border-rose-400" data-testid="portal-photo-pick">
          <Camera className="h-8 w-8 mb-1" /><span className="text-sm">Foto maken / kiezen</span>
          <input type="file" accept="image/*" capture="environment" className="hidden" onChange={pick} data-testid="portal-photo-input" />
        </label>
      )}
      <div className="flex items-start gap-2">
        <Textarea rows={2} value={desc} onChange={(e) => setDesc(e.target.value)} placeholder="Wat is hier mis? (typen of inspreken)" data-testid="portal-photo-desc" />
        <VoiceButton transcribe={transcribe} onText={(t) => setDesc((d) => (d ? d + " " : "") + t)} testId="portal-photo-voice" label="" />
      </div>
      <Button className="w-full bg-rose-600 hover:bg-rose-700" disabled={!file || busy} onClick={submit} data-testid="portal-photo-add"><Plus className="h-4 w-4 mr-1" /> {busy ? "Uploaden…" : "Foto toevoegen"}</Button>
    </div>
  );
}
